import { useCallback, useMemo } from "react";
import { Flex } from "@radix-ui/themes";
import { toast } from "sonner";
import Select, { SelectOption } from "@/components/shared/select/Select";
import { UpsertReleaseLoginEnforcementDto } from "@/v2/entities/release/Release.model";
import { useSimulator } from "@/v2/features/simulator/context/SimulatorWrapper";
import { FieldGroup, OptionalFieldGroup } from "../parts/FieldGroup";
import { OptionalTextarea } from "../parts/OptionalTextarea";
import styles from "../AutomationConfiguration.module.scss";

const maxAttemptsOptions: SelectOption[] = [
  ...[...Array(5)].map((_, i) => ({
    label: `${i + 1} attempt${i > 0 ? "s" : ""}`,
    value: (i + 1).toString(),
  })),
];

export function QuackchatLoginEnforcement() {
  const { updateRelease, currentRelease } = useSimulator();

  const loginEnforcement = useMemo(
    () => currentRelease?.loginEnforcement,
    [currentRelease]
  );

  const handleOnChange = useCallback(
    (changeset: Partial<UpsertReleaseLoginEnforcementDto>) => {
      if (currentRelease?.id) {
        updateRelease(String(currentRelease.id), {
          simulatorId: currentRelease.simulatorId,
          loginEnforcement: {
            ...loginEnforcement,
            ...changeset,
          },
        });
      } else {
        toast.error("Something went wrong, please try again");
      }
    },
    [currentRelease, loginEnforcement, updateRelease]
  );

  const isLoginEnforcementEnabled = loginEnforcement?.enabled ?? false;

  return (
    <OptionalFieldGroup
      label="Login Enforcement"
      description="Configure login enforcement settings for the chat."
      enabled={isLoginEnforcementEnabled}
      toggleEnabled={(nextValue) => {
        handleOnChange({ enabled: nextValue });
      }}
    >
      {isLoginEnforcementEnabled && (
        <Flex width={"100%"} direction={"column"} gap={"2"}>
          <FieldGroup
            label={"Max attempts"}
            description={
              "How many times a user can attempt to login before being blocked."
            }
          >
            <Select
              options={maxAttemptsOptions}
              value={loginEnforcement?.maxAttempts?.toString()}
              onChange={(newValue) => {
                handleOnChange({ maxAttempts: parseInt(newValue) });
              }}
              classes={{ trigger: styles.Select }}
            />
          </FieldGroup>

          <FieldGroup
            label={"in what conditions we close the chat ?"}
            description={"Add here the conditions in which we close the chat."}
          >
            <Flex width={"100%"} px="2px">
              <OptionalTextarea
                value={loginEnforcement?.closingCondition}
                onChange={(newValue) => {
                  handleOnChange({ closingCondition: newValue });
                }}
                buttonText={"Add condition"}
                label={"Conditions"}
              />
            </Flex>
          </FieldGroup>

          <FieldGroup
            label={"in what conditions we provide support ?"}
            description={"Add here the conditions in which we close the chat."}
          >
            <Flex width={"100%"} px="2px">
              <OptionalTextarea
                value={loginEnforcement?.supportCondition}
                onChange={(newValue) => {
                  handleOnChange({ supportCondition: newValue });
                }}
                buttonText={"Add condition"}
                label={"Conditions"}
              />
            </Flex>
          </FieldGroup>

          <FieldGroup
            label={"brief for the block"}
            description={"Add here the brief for the block."}
          >
            <Flex width={"100%"} px="2px">
              <OptionalTextarea
                value={loginEnforcement?.brief}
                onChange={(newValue) => {
                  handleOnChange({ brief: newValue });
                }}
                buttonText={"Add brief"}
                label={"Brief"}
              />
            </Flex>
          </FieldGroup>
        </Flex>
      )}
    </OptionalFieldGroup>
  );
}
